import { useEffect, useRef, useState } from "react";

/**
 * A dropdown's open state, closing as the composer's menus do: on a press
 * outside it or Escape (which hands focus back to the button). Up and Down move
 * between the items, Home and End jump to the ends; opening focuses the first.
 * Spread `buttonProps` on the trigger and put `ref` on the element that holds
 * both the trigger and the list.
 */
export function useMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const items = () => [...(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    items()[0]?.focus({ preventScroll: true });
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
        return;
      }
      const list = items();
      if (list.length === 0 || !["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
      if (!ref.current?.contains(document.activeElement)) return;
      e.preventDefault();
      const at = list.indexOf(document.activeElement as HTMLElement);
      const next =
        e.key === "Home" ? 0 : e.key === "End" ? list.length - 1 : e.key === "ArrowDown" ? (at + 1) % list.length : (at - 1 + list.length) % list.length;
      list[next]?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return {
    open,
    close: () => setOpen(false),
    ref,
    buttonProps: {
      ref: buttonRef,
      "aria-haspopup": "menu" as const,
      "aria-expanded": open,
      onClick: () => setOpen((v) => !v),
    },
  };
}
